import { Location } from 'history'
import { AdaptiveLearningContext } from '../types'

type QueryValue = string | (string | null)[] | null | undefined
type AdaptiveLearningLocationContext = Pick<Location<AdaptiveLearningContext>, 'state' | 'search'>

const getQueryValue = (value: QueryValue) => {
  if (Array.isArray(value)) return value.find((item): item is string => typeof item === 'string')
  return value ?? undefined
}

const getAdaptiveLearningContextFromValues = (
  source?: AdaptiveLearningContext,
  fallback?: AdaptiveLearningContext
): AdaptiveLearningContext => ({
  moduleId: getQueryValue(source?.moduleId) ?? getQueryValue(fallback?.moduleId),
  sessionId: getQueryValue(source?.sessionId) ?? getQueryValue(fallback?.sessionId),
  assessmentId: getQueryValue(source?.assessmentId) ?? getQueryValue(fallback?.assessmentId),
})

const getAdaptiveLearningSearchContext = (search?: string): AdaptiveLearningContext => {
  const queryParams = new URLSearchParams(search)

  return getAdaptiveLearningContextFromValues({
    moduleId: queryParams.get('moduleId') ?? undefined,
    sessionId: queryParams.has('sessionId') ? queryParams.get('sessionId') ?? '' : undefined,
    assessmentId: queryParams.get('assessmentId') ?? undefined,
  })
}

export const getAdaptiveLearningContext = (
  source?: AdaptiveLearningContext,
  fallback?: AdaptiveLearningContext
): AdaptiveLearningContext => getAdaptiveLearningContextFromValues(source, fallback)

export const getAdaptiveLearningContextFromLocation = ({
  state,
  search,
}: AdaptiveLearningLocationContext): AdaptiveLearningContext =>
  getAdaptiveLearningContextFromValues(state, getAdaptiveLearningSearchContext(search))

export const buildAdaptiveLearningQueryString = ({
  moduleId,
  sessionId,
  assessmentId,
}: AdaptiveLearningContext) => {
  const urlSearchParams = new URLSearchParams()

  if (moduleId !== undefined) urlSearchParams.append('moduleId', moduleId)
  if (sessionId !== undefined) urlSearchParams.append('sessionId', sessionId)
  if (assessmentId !== undefined) urlSearchParams.append('assessmentId', assessmentId)

  const adaptiveLearningQueryString = urlSearchParams.toString()
  return adaptiveLearningQueryString ? `?${adaptiveLearningQueryString}` : ''
}

export const isValidAdaptiveLearningContext = ({
  moduleId,
  sessionId,
  assessmentId,
}: AdaptiveLearningContext) => Boolean(moduleId) && sessionId !== undefined && Boolean(assessmentId)
